<template>
  <div class="page-container-wide">
    <div v-if="!isAdmin" class="empty-state">
      <p class="text-tertiary">{{ $t('admin_required') }}</p>
    </div>

    <template v-else>
      <h1 class="page-title">{{ $t('admin') }}</h1>

      <div class="admin-tabs mb-3">
        <button :class="['tab-btn', { active: tab === 'posts' }]" @click="tab = 'posts'">{{ $t('manage_posts') }}</button>
        <button :class="['tab-btn', { active: tab === 'comments' }]" @click="tab = 'comments'">{{ $t('manage_comments') }}</button>
        <button :class="['tab-btn', { active: tab === 'password' }]" @click="tab = 'password'">{{ $t('change_password') }}</button>
        <button :class="['tab-btn', { active: tab === 'about' }]" @click="tab = 'about'; loadAbout()">关于页面</button>
        <button :class="['tab-btn', { active: tab === 'contacts' }]" @click="tab = 'contacts'; loadContacts()">联系方式</button>
      </div>

      <!-- Posts Management -->
      <div v-if="tab === 'posts'">
        <button class="btn btn-primary mb-2" @click="newPost">{{ $t('new_post') }}</button>

        <div v-if="editingPost !== null" class="editor-box mb-3">
          <h3>{{ editingPost.id ? $t('edit_post') : $t('new_post') }}</h3>
          <input v-model="editTitle" class="input-field mt-1" :placeholder="$t('post_title')" />
          <div class="mt-2">
            <mavon-editor
              v-model="editContent"
              :language="editorLang"
              :toolbars="toolbars"
              @imgAdd="handleImgAdd"
              style="min-height:400px;z-index:1"
            />
          </div>
          <div class="editor-options mt-1">
            <label class="editor-check"><input type="checkbox" v-model="optAllowComments" /> 开启评论</label>
            <label class="editor-check"><input type="checkbox" v-model="optAllowLikes" /> 收集点赞</label>
            <label class="editor-check"><input type="checkbox" v-model="optTrackViews" /> 收集浏览数</label>
            <label class="editor-check"><input type="checkbox" v-model="optTrackVisitors" /> 收集访客数据</label>
          </div>
          <div class="mt-2" style="display:flex;gap:8px;">
            <button class="btn btn-primary" @click="savePost" :disabled="saving">
              {{ saving ? '...' : $t('save') }}
            </button>
            <button class="btn" @click="cancelEdit">{{ $t('cancel') }}</button>
          </div>
        </div>

        <div v-if="posts.length === 0" class="text-tertiary text-sm">{{ $t('no_posts') }}</div>
        <div v-else class="admin-list">
          <div v-for="post in posts" :key="post.id" class="admin-list-item">
            <span class="admin-item-title">{{ post.title }}</span>
            <span class="text-tertiary text-xs">{{ formatDate(post.created_at) }}</span>
            <div class="admin-item-actions">
              <button class="btn btn-sm" @click="editPost(post)">{{ $t('edit') }}</button>
              <button class="btn btn-sm" @click="$router.push('/admin/stats/'+post.id)">数据</button>
              <button class="btn btn-sm btn-danger" @click="confirmDeletePost(post)">{{ $t('delete') }}</button>
            </div>
          </div>
        </div>
        <div v-if="postTotalPages > 1" class="pagination mt-2">
          <button class="btn btn-sm" :disabled="postPage <= 1" @click="changePostPage(postPage - 1)">&larr;</button>
          <span class="text-sm text-secondary">{{ postPage }} / {{ postTotalPages }}</span>
          <button class="btn btn-sm" :disabled="postPage >= postTotalPages" @click="changePostPage(postPage + 1)">&rarr;</button>
        </div>
      </div>

      <!-- Comments Management -->
      <div v-if="tab === 'comments'">
        <p class="text-tertiary text-sm mb-2">{{ $t('manage_comments') }}</p>
        <!-- Simple: show all posts to select and view their comments -->
        <div v-if="adminPosts.length === 0" class="text-tertiary text-sm">{{ $t('no_posts') }}</div>
        <div v-else class="admin-list">
          <div v-for="post in adminPosts" :key="post.id" class="admin-list-item">
            <span class="admin-item-title">{{ post.title }}</span>
            <button class="btn btn-sm" @click="viewPostComments(post)">{{ $t('comment') }}</button>
          </div>
        </div>
        <div v-if="viewingPostTitle" class="mt-3">
          <h3 class="mb-2">{{ viewingPostTitle }}</h3>
          <div v-if="commentsLoading" class="text-tertiary text-sm text-center" style="padding:24px 0">Loading...</div>
          <div v-else-if="viewingComments.length === 0" class="text-tertiary text-sm text-center" style="padding:24px 0">{{ $t('no_comments') }}</div>
          <div v-else>
            <div v-for="c in viewingComments" :key="c.id" class="admin-comment-item">
              <div>
                <strong>{{ c.username }}</strong>
                <span v-if="c.pinned" style="color:var(--text-primary)">&#x1F4CC;</span>
                <span class="text-tertiary text-xs ml-1">{{ c.like_count || 0 }} 赞 | {{ formatDate(c.created_at) }}</span>
                <span v-if="c.children && c.children.length" class="text-tertiary text-xs ml-1">| {{ c.children.length }} 回复</span>
              </div>
              <p class="text-sm mt-1">{{ c.content }}</p>
              <div style="display:flex;gap:6px;margin-top:4px">
                <button class="btn btn-sm" @click="$router.push('/comment/'+c.id+'?admin=1')">详情</button>
                <button class="btn btn-sm btn-danger" @click="deleteCommentAction(c.id)">{{ $t('delete') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Change Password -->
      <div v-if="tab === 'password'">
        <div class="password-box">
          <input v-model="oldPassword" type="password" class="input-field" :placeholder="$t('old_password')" />
          <input v-model="newPassword" type="password" class="input-field mt-1" :placeholder="$t('new_password')" />
          <button class="btn btn-primary mt-2" @click="changePasswordAction" :disabled="changingPwd">
            {{ changingPwd ? '...' : $t('save') }}
          </button>
          <p v-if="pwdMsg" class="text-sm mt-1" :style="{ color: pwdOk ? 'inherit' : '#d32f2f' }">{{ pwdMsg }}</p>
        </div>
      </div>

      <!-- About page editor -->
      <div v-if="tab === 'about'">
        <h3 class="mb-2">关于页面内容</h3>
        <div v-if="aboutLoading" class="text-tertiary text-sm">Loading...</div>
        <div v-else>
          <mavon-editor v-model="aboutContent" :language="editorLang" :toolbars="toolbars" style="min-height:400px;z-index:1" />
          <button class="btn btn-primary mt-2" @click="saveAbout" :disabled="aboutSaving">{{ aboutSaving ? '...' : $t('save') }}</button>
          <p v-if="aboutMsg" class="text-sm mt-1">{{ aboutMsg }}</p>
        </div>
      </div>

      <!-- Contacts editor -->
      <div v-if="tab === 'contacts'">
        <h3 class="mb-2">联系方式管理</h3>
        <div v-if="contactsLoading" class="text-tertiary text-sm">Loading...</div>
        <div v-else class="contacts-editor">
          <div v-for="f in contactFields" :key="f.key" class="contact-edit-row">
            <span class="contact-edit-label">{{ f.label }}</span>
            <input v-model="contactsData[f.key]" class="input-field" style="max-width:300px;padding:6px 10px;font-size:0.85rem" :placeholder="'输入 '+f.label" />
          </div>
          <button class="btn btn-primary mt-2" @click="saveContacts" :disabled="contactsSaving">{{ contactsSaving ? '...' : $t('save') }}</button>
          <p v-if="contactsMsg" class="text-sm mt-1">{{ contactsMsg }}</p>
        </div>
      </div>

      <!-- Delete Post Confirm Modal -->
      <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
        <div class="modal-box">
          <div class="modal-title">{{ $t('confirm_delete') }}</div>
          <p class="text-sm text-secondary mb-2">{{ $t('delete_post_confirm') }}</p>
          <div class="modal-actions">
            <button class="btn" @click="deleteTarget = null">{{ $t('cancel') }}</button>
            <button class="btn btn-danger" @click="deletePostAction">{{ $t('delete') }}</button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getPosts, getPost, createPost, updatePost, deletePost, getComments, deleteComment, changePassword, uploadImage, getAboutSettings, saveAboutSettings, getContactsSettings, saveContactsSettings } from '../api'

export default {
  name: 'Admin',
  data() {
    return {
      tab: 'posts',
      posts: [],
      postPage: 1,
      postTotal: 0,
      postTotalPages: 0,
      editingPost: null,
      editTitle: '',
      editContent: '',
      optAllowComments: true,
      optAllowLikes: false,
      optTrackViews: false,
      optTrackVisitors: true,
      saving: false,
      deleteTarget: null,
      // Comments view
      adminPosts: [],
      viewingComments: [],
      viewingPostTitle: '',
      commentsLoading: false,
      // Password
      oldPassword: '',
      newPassword: '',
      changingPwd: false,
      pwdMsg: '',
      pwdOk: false,
      aboutContent: '',
      aboutLoading: false,
      aboutSaving: false,
      aboutMsg: '',
      contactFields: [
        { key: 'github', label: 'GitHub' }, { key: 'email', label: 'Email' },
        { key: 'wechat', label: 'WeChat' }, { key: 'qq', label: 'QQ' }, { key: 'qq_group', label: 'QQ Group' },
        { key: 'telegram', label: 'Telegram' }, { key: 'x', label: 'X (Twitter)' }, { key: 'phone', label: 'Phone' },
        { key: 'facebook', label: 'Facebook' }, { key: 'douyin', label: 'Douyin' }, { key: 'tiktok', label: 'TikTok' },
        { key: 'instagram', label: 'Instagram' }, { key: 'bilibili', label: 'Bilibili' }, { key: 'youtube', label: 'YouTube' }
      ],
      contactsData: {},
      contactsLoading: false,
      contactsSaving: false,
      contactsMsg: '',
      toolbars: {
        bold: true, italic: true, header: true, underline: true, strikethrough: true,
        mark: true, superscript: true, subscript: true, quote: true, ol: true,
        ul: true, link: true, imagelink: true, code: true, table: true,
        fullscreen: true, readmodel: true, undo: true, redo: true,
        trash: true, save: false, navigation: true, alignleft: true,
        aligncenter: true, alignright: true, subfield: true, preview: true
      }
    }
  },
  computed: {
    isAdmin() {
      return localStorage.getItem('markjy_is_admin') === 'true'
    },
    editorLang() {
      const map = { 'zh-CN': 'zh-CN', 'zh-TW': 'zh-TW', 'en': 'en' }
      return map[this.$i18nState.lang] || 'zh-CN'
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return ''
      const date = new Date(d)
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    fetchPosts() {
      getPosts(this.postPage, 10).then(res => {
        const d = res.data.data
        this.posts = d.posts || []
        this.postTotal = d.total
        this.postTotalPages = d.total_pages
      })
    },
    changePostPage(p) {
      this.postPage = p
      this.fetchPosts()
    },
    newPost() {
      this.editingPost = { id: null }
      this.editTitle = ''
      this.editContent = ''
      this.optAllowComments = true
      this.optAllowLikes = false
      this.optTrackViews = false
      this.optTrackVisitors = true
    },
    editPost(post) {
      this.editingPost = { ...post }
      this.editTitle = post.title
      this.editContent = post.content
      this.optAllowComments = post.allow_comments !== false
      this.optAllowLikes = !!post.allow_likes
      this.optTrackViews = !!post.track_views
      this.optTrackVisitors = post.track_visitors !== false
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    cancelEdit() {
      this.editingPost = null
      this.editTitle = ''
      this.editContent = ''
    },
    savePost() {
      if (!this.editTitle || !this.editContent) return
      this.saving = true
      const opts = {
        allow_comments: this.optAllowComments,
        allow_likes: this.optAllowLikes,
        track_views: this.optTrackViews,
        track_visitors: this.optTrackVisitors
      }
      const action = this.editingPost.id
        ? updatePost(this.editingPost.id, this.editTitle, this.editContent, opts)
        : createPost(this.editTitle, this.editContent, opts)

      action.then(() => {
        this.cancelEdit()
        this.fetchPosts()
        this.fetchAdminPosts()
      }).catch(() => {
        alert(this.$t('server_error'))
      }).finally(() => { this.saving = false })
    },
    confirmDeletePost(post) {
      this.deleteTarget = post
    },
    deletePostAction() {
      if (!this.deleteTarget) return
      deletePost(this.deleteTarget.id).then(() => {
        this.deleteTarget = null
        this.fetchPosts()
        this.fetchAdminPosts()
      })
    },
    fetchAdminPosts() {
      getPosts(1, 50).then(res => {
        this.adminPosts = (res.data.data && res.data.data.posts) || []
      })
    },
    viewPostComments(post) {
      this.viewingPostTitle = post.title
      this.viewingComments = []
      this.commentsLoading = true
      getComments(post.id).then(res => {
        this.viewingComments = (res.data.data || []).filter(c => !c.parent_id)
      }).finally(() => {
        this.commentsLoading = false
      })
    },
    deleteCommentAction(id) {
      if (!confirm(this.$t('delete_comment_confirm'))) return
      deleteComment(id).then(() => {
        this.viewingComments = this.viewingComments.filter(c => c.id !== id)
      })
    },
    loadAbout() {
      if (this.aboutContent) return
      this.aboutLoading = true
      getAboutSettings().then(res => { this.aboutContent = res.data.data.content || '' }).finally(() => { this.aboutLoading = false })
    },
    loadContacts() {
      if (Object.keys(this.contactsData).length) return
      this.contactsLoading = true
      getContactsSettings().then(res => {
        try { this.contactsData = JSON.parse(res.data.data.contacts || '{}') } catch { this.contactsData = {} }
      }).finally(() => { this.contactsLoading = false })
    },
    saveContacts() {
      this.contactsSaving = true; this.contactsMsg = ''
      saveContactsSettings(this.contactsData).then(() => { this.contactsMsg = '保存成功' }).catch(() => { this.contactsMsg = '保存失败' }).finally(() => { this.contactsSaving = false })
    },
    saveAbout() {
      this.aboutSaving = true; this.aboutMsg = ''
      saveAboutSettings(this.aboutContent).then(() => { this.aboutMsg = '保存成功' }).catch(() => { this.aboutMsg = '保存失败' }).finally(() => { this.aboutSaving = false })
    },
    changePasswordAction() {
      if (!this.oldPassword || !this.newPassword) {
        this.pwdMsg = '请填写旧密码和新密码'
        this.pwdOk = false
        return
      }
      if (this.newPassword.length < 6 || this.newPassword.length > 24) {
        this.pwdMsg = this.$t('password_rule')
        this.pwdOk = false
        return
      }
      this.changingPwd = true
      changePassword(this.oldPassword, this.newPassword).then(() => {
        this.pwdMsg = '密码修改成功'
        this.pwdOk = true
        this.oldPassword = ''
        this.newPassword = ''
      }).catch(err => {
        const msg = err.response && err.response.data ? err.response.data.message : this.$t('server_error')
        this.pwdMsg = msg
        this.pwdOk = false
      }).finally(() => { this.changingPwd = false })
    },
    async handleImgAdd(pos, file) {
      try {
        const res = await uploadImage(file)
        const url = res.data.data.url
        this.$refs.mdeditor && this.$refs.mdeditor.$img2Url(pos, url)
      } catch {
        alert(this.$t('image_upload_fail'))
      }
    }
  },
  mounted() {
    if (!this.isAdmin) {
      this.$router.replace('/')
      return
    }
    this.fetchPosts()
    this.fetchAdminPosts()
  }
}
</script>

<style scoped>
.page-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 20px;
}
.admin-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0;
}
.tab-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.15s ease;
}
.tab-btn:hover { color: var(--text-primary); }
.tab-btn.active {
  color: var(--text-primary);
  border-bottom-color: var(--text-primary);
}
.editor-box h3 {
  font-size: 1rem;
  font-weight: 600;
}
.admin-list {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}
.admin-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-light);
}
.admin-list-item:last-child { border-bottom: none; }
.admin-item-title {
  flex: 1;
  font-size: 0.9rem;
}
.admin-item-actions {
  display: flex;
  gap: 6px;
}
.admin-comment-item {
  padding: 12px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  margin-bottom: 8px;
}
.password-box {
  max-width: 380px;
}
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 0;
}
.modal-overlay {
  position: fixed; inset: 0; background: var(--overlay);
  z-index: 2000; display: flex; align-items: center; justify-content: center;
}
.modal-box {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 12px; padding: 24px; max-width: 380px; width: 90%;
}
.modal-title { font-size: 1.05rem; font-weight: 600; margin-bottom: 8px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; }
.editor-options { display: flex; flex-wrap: wrap; gap: 12px; }
.editor-check { font-size: 0.8rem; color: var(--text-secondary); display: flex; align-items: center; gap: 4px; cursor: pointer; }
.editor-check input { accent-color: var(--text-primary); cursor: pointer; }
.contacts-editor { max-width: 500px; }
.contact-edit-row { display: flex; align-items: center; gap: 12px; padding: 6px 0; }
.contact-edit-label { width: 110px; font-size: 0.85rem; color: var(--text-secondary); flex-shrink: 0; }
.empty-state { padding: 80px 0; text-align: center; }
</style>
